import * as React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { TreeItemLabel } from '@mui/x-tree-view/TreeItem';
import { Typography } from '@mui/joy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { StatusTooltip } from './StatusTooltip';
import { RemarkIndicator, RemarkItem } from './RemarkPopover';
import { FileStatus } from './FileStatus';
import { parseFolderName } from './folderName';
import './CustomLabel.scss';

const DUMMY_TAGS: Array<{ label: string, loading: boolean }> = [
  // { label: 'Extracted', loading: false },
  // { label: 'Consolidated Contract', loading: false },
  // { label: 'Consolidated', loading: false },
  // { label: 'Summary', loading: false },
  // { label: 'Consolidating', loading: true },
  // { label: 'Summary', loading: true },
];

const TagChip = styled(Box)<{ loading?: number }>(({ theme, loading }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '1px 6px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 400,
  lineHeight: 1.6,
  backgroundColor: loading
    ? theme.palette.action.selected
    : theme.palette.success.light + '33',
  color: loading ? theme.palette.text.secondary : theme.palette.success.dark,
}));

/** The parties to the contract, read off the folder name. Neutral on purpose:
 *  it names counterparties, it doesn't report a status like TagChip does. */
const PartyChip = styled(Box)(() => ({
  display: 'inline-block',
  maxWidth: 260,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: '1px 8px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1.6,
  backgroundColor: 'var(--joy-palette-neutral-softBg, #f0f4f8)',
  color: 'var(--joy-palette-neutral-softColor, #32383e)',
  border: '1px solid var(--joy-palette-neutral-outlinedBorder, #dde7ee)',
  fontFamily: 'var(--joy-fontFamily-body)',
}));

/** Marks a file the backend has matched to other copies of the same document.
 *  Warning-toned, not danger: it is a thing to look at, not a failure. */
const DuplicateChip = styled(Box)(() => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1.6,
  whiteSpace: 'nowrap',
  backgroundColor: 'var(--joy-palette-warning-softBg, #fdf0e1)',
  color: 'var(--joy-palette-warning-softColor, #492b08)',
  border: '1px solid var(--joy-palette-warning-outlinedBorder, #f3c896)',
  fontFamily: 'var(--joy-fontFamily-body)',
}));

const TreeItemLabelText = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  fontFamily: 'var(--joy-fontFamily-body)',
  fontWeight: 400,
  fontStyle: 'normal',
  letterSpacing: 0,
  fontSize: 14,
  color: theme.palette.text.primary,
  width: '100%',
  justifyContent: 'space-between',
}));

interface CustomLabelProps {
  children: React.ReactNode;
  icon?: React.ElementType;
  expandable?: boolean;
  item?: any;
  /** Rendered before the status icons (remark dot / tick) */
  preStatusAdornment?: React.ReactNode;
  /** Rendered after the status icons, at the trailing edge of the row */
  endAdornment?: React.ReactNode;
  /** When true, the name becomes an inline text input (folder rename). */
  isRenaming?: boolean;
  /** Called with the new name when the rename is confirmed (Enter / blur). */
  onRenameCommit?: (name: string) => void;
  /** Called when the rename is abandoned (Escape). */
  onRenameCancel?: () => void;
  /** Validation or API message for the rename in progress. */
  renameError?: string | null;
  /** True while the rename request is in flight. */
  isRenameSaving?: boolean;
}

/**
 * Inline rename field. Uncontrolled so keystrokes don't re-render the row:
 * Enter commits, Escape cancels, and losing focus commits — except when the
 * blur came from selecting text (see below). Pointer/key events are kept from
 * bubbling so typing doesn't select, expand, or drag the tree row.
 *
 * A rejected rename arrives back as `error`. That reopens the field for another
 * attempt — the commit guard is released and focus returned — with the typed
 * value still in place, so nothing has to be retyped.
 */
const RenameInput = ({
  initialName,
  error,
  isSaving,
  onCommit,
  onCancel,
}: {
  initialName: string;
  error?: string | null;
  isSaving?: boolean;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const committedRef = React.useRef(false);
  /** True from a press inside the field until the gesture has fully settled. */
  const selectingRef = React.useRef(false);
  const errorId = React.useId();

  React.useEffect(() => {
    // Defer a frame: the context menu that triggered the rename restores focus
    // to its trigger as it unmounts, which would otherwise steal it back.
    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  React.useEffect(() => {
    if (!error) return;
    // Let the user try again: blur already fired the failed attempt, so the
    // guard has to be released and focus brought back to the field.
    committedRef.current = false;
    inputRef.current?.focus();
  }, [error]);

  /**
   * Selecting the whole name by dragging often ends with the pointer released
   * outside the field, on the tree row — which takes focus, blurring the input.
   * That blur is part of a selection, not a decision to save, so it is ignored
   * and focus is taken back.
   *
   * The flag has to outlive the pointer-up: the row only claims focus on the
   * click that follows it, so it is cleared a frame later, once that has played
   * out. A press that starts outside the field never sets it, which leaves the
   * ordinary click-away-to-save behaviour intact.
   */
  React.useEffect(() => {
    const endSelection = () => {
      if (!selectingRef.current) return;
      requestAnimationFrame(() => {
        selectingRef.current = false;
        const input = inputRef.current;
        if (input && document.activeElement !== input) input.focus();
      });
    };

    window.addEventListener('pointerup', endSelection);
    window.addEventListener('pointercancel', endSelection);
    return () => {
      window.removeEventListener('pointerup', endSelection);
      window.removeEventListener('pointercancel', endSelection);
    };
  }, []);

  const finish = (commit: boolean) => {
    if (committedRef.current || isSaving) return;
    committedRef.current = true;
    if (commit) onCommit(inputRef.current?.value ?? '');
    else onCancel();
  };

  return (
    <div className="tree-label-rename">
      <input
        ref={inputRef}
        className={
          error
            ? 'tree-label-rename-input tree-label-rename-input--error'
            : 'tree-label-rename-input'
        }
        defaultValue={initialName}
        readOnly={isSaving}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        // Drag sensors activate on pointerdown, not mousedown: without this a
        // drag-select inside the field would also reach the row's drag handle.
        onPointerDown={(e) => {
          selectingRef.current = true;
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            finish(false);
          }
        }}
        onBlur={() => {
          // Mid-selection: the gesture handler above will hand focus back.
          if (selectingRef.current) return;
          finish(true);
        }}
      />
      {error && (
        <span id={errorId} role="alert" className="tree-label-rename-error">
          {error}
        </span>
      )}
    </div>
  );
};

const splitNameAndExtension = (name: string) => {
  const lastDotIndex = name.lastIndexOf('.');
  if (lastDotIndex > 0 && lastDotIndex < name.length - 1) {
    return {
      nameWithoutExt: name.slice(0, lastDotIndex),
      extension: name.slice(lastDotIndex),
    };
  }
  return { nameWithoutExt: name, extension: '' };
};

export const CustomLabel = React.memo(
  ({
    icon: Icon,
    expandable,
    children,
    item,
    preStatusAdornment,
    endAdornment,
    isRenaming,
    onRenameCommit,
    onRenameCancel,
    renameError,
    isRenameSaving,
    ...other
  }: CustomLabelProps) => {
    const { t } = useTranslation();
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [tooltipTitle, setTooltipTitle] = React.useState('');

    const fullName = children?.toString() || '';
    const isFolder = item?.fileType === 'folder';

    // Folders carry their parties in the name ("... between A and B"); the
    // parties show as a tag, so only the part before "between" is the title.
    const { title, parties } = React.useMemo(
      () =>
        isFolder
          ? parseFolderName(fullName)
          : { title: fullName, parties: null },
      [isFolder, fullName]
    );

    const { nameWithoutExt, extension } = splitNameAndExtension(title);

    const mainStatus =
      item?.fileData?.status?.code || item?.folderData?.status || null;

    const remark: RemarkItem[] =
      item?.fileData?.remark || item?.folderData?.remarks || [];

    /* The Duplicates section is where copies are reconciled; the tree only says
       that this file has copies, so the user knows before acting on it. */
    const isDuplicate = !isFolder && !!item?.fileData?.is_duplicate;

    /**
     * Only truncated names get a tooltip, and that can only be decided by
     * measuring. Measuring on mount cost a forced layout, an extra render and a
     * window `resize` listener for every row in the tree — hundreds of them
     * once expanded. The tooltip asks the question instead: MUI opens on
     * mouseover regardless of the title and simply renders nothing while the
     * title is empty, so measuring in `onOpen` is both cheaper and always up to
     * date (no resize listener needed).
     */
    const handleTooltipOpen = React.useCallback(() => {
      const el = textRef.current;
      if (!el) return;
      const overflowing = el.scrollWidth > el.clientWidth;
      // The parties were lifted out of the name into a tag, so a truncated
      // title is shown back in full — name and parties both.
      const next = overflowing
        ? (parties ? fullName : el.textContent) ?? ''
        : '';
      setTooltipTitle((prev) => (prev === next ? prev : nameWithoutExt + extension));
    }, [fullName, parties]);

    return (
      <div className="tree-label-root">
        <TreeItemLabel {...other} className="tree-label-line">
          {/* Colour comes from CustomLabel.scss, not a prop: pinned to #555E68
              the icon vanished into a selected row, whose fill is the very
              same slate in dark mode. */}
          {Icon && (
            <Box component={Icon} className="labelIcon tree-label-icon" />
          )}

          <div
            className={
              parties && !isRenaming
                ? 'tree-label-main tree-label-main--parties'
                : 'tree-label-main'
            }
          >
            <StatusTooltip
              title={tooltipTitle}
              onOpen={handleTooltipOpen}
              disableInteractive
              leaveDelay={0}
            >
              <TreeItemLabelText>
                <div className="tree-label-name">
                  {isRenaming && onRenameCommit && onRenameCancel ? (
                    <RenameInput
                      initialName={fullName}
                      error={renameError}
                      isSaving={isRenameSaving}
                      onCommit={onRenameCommit}
                      onCancel={onRenameCancel}
                    />
                  ) : (
                    <>
                      <Typography
                        level={isFolder ? 'body-lg' : 'body-sm'}
                        ref={textRef}
                        className={
                          isFolder
                            ? 'tree-label-name__text tree-label-name__text--folder'
                            : 'tree-label-name__text'
                        }
                      >
                        {nameWithoutExt}
                      </Typography>
                      <Typography
                        level={isFolder ? 'body-lg' : 'body-sm'}
                        className={
                          isFolder
                            ? 'tree-label-name__ext tree-label-name__ext--folder'
                            : 'tree-label-name__ext'
                        }
                      >
                        {extension}
                      </Typography>
                      {isDuplicate && (
                        <DuplicateChip className="tree-label-duplicate">
                          {t('Duplicate')}
                        </DuplicateChip>
                      )}
                    </>
                  )}
                </div>
              </TreeItemLabelText>
            </StatusTooltip>
            {parties && !isRenaming && (
              <div className="tree-label-tags tree-label-parties">
                <PartyChip title={parties}>{parties}</PartyChip>
              </div>
            )}
            {!isFolder && DUMMY_TAGS.length > 0 && (
              <div className="tree-label-tags">
                {DUMMY_TAGS.map((tag) => (
                  <TagChip key={tag.label} loading={tag.loading ? 1 : 0}>
                    {tag.loading ? (
                      <CircularProgress
                        size={12}
                        thickness={6}
                        sx={{ color: 'text.primary' }}
                      />
                    ) : (
                      <CheckCircleIcon sx={{ fontSize: 12 }} color="success" />
                    )}
                    {tag.label}
                  </TagChip>
                ))}
              </div>
            )}
          </div>

          {preStatusAdornment}
          {remark.length > 0 && <RemarkIndicator remark={remark} />}
          {mainStatus && <FileStatus status={mainStatus} />}
          {endAdornment}
        </TreeItemLabel>
      </div>
    );
  }
);
